<template>
  <ManagerLayout>
    <div class="trial-progress">
      <h2 class="page-title">体験期間ダッシュボード</h2>
      <p class="page-desc">
        横軸は各生徒がDay1の課題・練習を提出した日からの経過日数（実際のカレンダー日）です。マスの色は、その日に提出されたカリキュラム上のDay内容の状況を表します。休みながら続いている・まとめて提出している、といった実際のペースが分かります。有料契約中の生徒は表示されません。
      </p>

      <section class="manager-section">
        <div class="card">
          <div class="toolbar">
            <span class="total-note">対象 {{ learners.length }} 名</span>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="loading" @click="load">
              {{ loading ? '読み込み中...' : '更新' }}
            </button>
          </div>

          <div v-if="loading" class="loading-state">読み込み中...</div>
          <div v-else-if="!learners.length" class="empty-state">体験期間中の生徒がいません</div>
          <template v-else>
            <div class="table-scroll">
              <table class="progress-table">
                <thead>
                  <tr>
                    <th class="name-col">生徒</th>
                    <th v-for="day in dayCount" :key="day">{{ day }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="l in learners" :key="l.learnerId">
                    <td class="name-col">
                      <div class="learner-name">{{ l.name || '—' }}</div>
                      <div class="learner-sub">{{ l.parentFullName || l.parentEmail }}</div>
                    </td>
                    <td
                      v-for="(d, idx) in l.days"
                      :key="idx"
                      class="day-cell"
                      :class="{ clickable: d }"
                      :title="cellTitle(l, idx + 1, d)"
                      @click="d && openDetail(l, idx + 1, d)"
                    >
                      <template v-if="d">
                        <div class="bar-sub" :class="subClass(d.s)"></div>
                        <div class="bar-play" :class="d.l ? 'played' : 'unplayed'"></div>
                      </template>
                      <div v-else class="empty-day"></div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="legend">
              <span><span class="swatch good"></span>提出：練習・課題とも済み</span>
              <span><span class="swatch warn"></span>提出：片方のみ</span>
              <span><span class="swatch bad"></span>未提出</span>
              <span><span class="swatch played"></span>フィードバック再生済み</span>
              <span><span class="swatch unplayed"></span>再生まだ</span>
            </div>
          </template>
        </div>
      </section>

      <div v-if="detail" class="detail-overlay" @click.self="detail = null">
        <div class="detail-card">
          <h3>{{ detail.name }} — {{ detail.dateLabel }}（{{ detail.colLabel }}日目）</h3>
          <p class="detail-row">
            提出状況：
            <strong>{{ detail.subLabel }}</strong>
          </p>
          <p class="detail-row">
            提出されたカリキュラムDay：
            <strong>{{ detail.curriculumDaysLabel }}</strong>
          </p>
          <p class="detail-row">
            フィードバック再生：
            <strong>{{ detail.played ? '再生済み' : '未再生' }}</strong>
          </p>
          <button type="button" class="btn btn-secondary btn-sm" @click="detail = null">閉じる</button>
        </div>
      </div>
    </div>
  </ManagerLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import ManagerLayout from '../../components/layout/ManagerLayout.vue'
import { supabase } from '../../supabaseClient'

const apiBase = import.meta.env.VITE_API_BASE_URL || ''

const learners = ref([])
const dayCount = ref(30)
const loading = ref(false)
const detail = ref(null)

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token
}

function subClass (s) {
  if (s === 2) return 'good'
  if (s === 1) return 'warn'
  return 'bad'
}

function subLabel (s) {
  if (s === 2) return '練習・課題とも提出済み'
  if (s === 1) return '片方のみ提出'
  return '未提出'
}

function curriculumDaysLabel (curriculumDays) {
  if (!curriculumDays || !curriculumDays.length) return 'なし（提出なし）'
  return curriculumDays.map((d) => `Day${d}`).join('、')
}

function cellTitle (l, col, d) {
  if (!d) return ''
  return `${d.date}（${col}日目）：${subLabel(d.s)}［${curriculumDaysLabel(d.curriculumDays)}］/ 再生${d.l ? '済み' : 'まだ'}`
}

function openDetail (l, col, d) {
  detail.value = {
    name: l.name || '—',
    dateLabel: d.date,
    colLabel: col,
    subLabel: subLabel(d.s),
    curriculumDaysLabel: curriculumDaysLabel(d.curriculumDays),
    played: !!d.l
  }
}

const load = async () => {
  loading.value = true
  try {
    const token = await getToken()
    const res = await fetch(`${apiBase}/api/manager/trial-progress`, {
      cache: 'no-store',
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      learners.value = []
      return
    }
    learners.value = data.learners || []
    dayCount.value = data.dayCount || 30
  } catch (_) {
    learners.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  load()
})
</script>

<style scoped>
.trial-progress {
  padding: 0;
}

.page-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  color: #1e293b;
}

.page-desc {
  margin: 0 0 1.5rem 0;
  color: #64748b;
  font-size: 0.9rem;
}

.manager-section {
  margin-bottom: 2rem;
}

.card {
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.total-note {
  font-size: 0.9rem;
  color: #64748b;
}

.loading-state,
.empty-state {
  padding: 2rem;
  text-align: center;
  color: #64748b;
}

.table-scroll {
  overflow-x: auto;
}

.progress-table {
  border-collapse: collapse;
  min-width: 640px;
  width: 100%;
}

.progress-table th,
.progress-table td {
  padding: 0;
}

.progress-table thead th {
  text-align: center;
  font-weight: 500;
  color: #64748b;
  font-size: 0.7rem;
  padding: 0 3px 8px;
}

.progress-table thead th.name-col {
  text-align: left;
  padding-left: 4px;
  min-width: 160px;
}

.progress-table tbody td.name-col {
  position: sticky;
  left: 0;
  background: white;
  padding: 0.6rem 0.75rem 0.6rem 0.25rem;
  border-top: 1px solid #e2e8f0;
  white-space: nowrap;
}

.progress-table tbody tr:first-child td.name-col {
  border-top: none;
}

.learner-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.85rem;
}

.learner-sub {
  font-size: 0.7rem;
  color: #94a3b8;
}

.day-cell {
  padding: 8px 3px;
  border-top: 1px solid #e2e8f0;
  min-width: 26px;
}

.progress-table tbody tr:first-child .day-cell {
  border-top: none;
}

.day-cell.clickable {
  cursor: pointer;
}

.day-cell.clickable:hover .bar-sub {
  opacity: 0.8;
}

.bar-sub {
  height: 11px;
  border-radius: 3px 3px 0 0;
}

.bar-play {
  height: 6px;
  border-radius: 0 0 3px 3px;
  margin-top: 2px;
}

.empty-day {
  height: 19px;
}

.bar-sub.good,
.swatch.good {
  background: #4c8c5b;
}

.bar-sub.warn,
.swatch.warn {
  background: #c98a2e;
}

.bar-sub.bad,
.swatch.bad {
  background: #c0524a;
}

.bar-play.played,
.swatch.played {
  background: #2b5f8a;
}

.bar-play.unplayed,
.swatch.unplayed {
  background: #c9c7bc;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 20px;
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 16px;
}

.legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.swatch {
  width: 13px;
  height: 13px;
  border-radius: 3px;
  display: inline-block;
}

.detail-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}

.detail-card {
  background: white;
  border-radius: 10px;
  padding: 1.5rem;
  min-width: 260px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.detail-card h3 {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  color: #1e293b;
}

.detail-row {
  margin: 0 0 0.75rem 0;
  font-size: 0.85rem;
  color: #334155;
}
</style>
